import { useRef, useState, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import styles from './index.module.scss';
import Play from 'src/assets/icons/Play';
import Pause from 'src/assets/icons/Pause';
import cx from 'classnames';

interface EmbeddedPlayerProps {
  url?: string;
}

const EmbeddedPlayer = ({ url }: EmbeddedPlayerProps) => {
  const waveFormRef = useRef<WaveSurfer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    // Don't initialize if no URL or container
    if (!url || !containerRef.current) return;

    // Create new WaveSurfer instance
    const wavesurfer = WaveSurfer.create({
      barWidth: 0.5,
      barGap: 0.5,
      barMinHeight: 1,
      cursorWidth: 1,
      container: containerRef.current,
      backend: 'WebAudio',
      height: 30,
      progressColor: '#FE6E00',
      waveColor: '#C4C4C4',
      cursorColor: '#fff'
    });

    waveFormRef.current = wavesurfer;

    wavesurfer.on('ready', () => {
      setDuration(wavesurfer.getDuration() || 0);
    });
    wavesurfer.on('play', () => setPlaying(true));
    wavesurfer.on('pause', () => setPlaying(false));
    wavesurfer.on('seek', (cursor: number) => {
      setSecondsElapsed(cursor * (wavesurfer.getDuration() || 0));
    });
    wavesurfer.on('audioprocess', (n: number) => setSecondsElapsed(n));
    wavesurfer.load(url);

    // Cleanup on unmount or URL change
    return () => {
      wavesurfer.destroy();
      waveFormRef.current = null;
    };
  }, [url]);

  const secondsToTimestamp = (seconds: number) => {
    seconds = Math.floor(seconds);
    const m: any = Math.floor(seconds / 60);
    const s: any = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handlePlayPause = () => waveFormRef.current?.playPause();

  return (
    <div className={cx(styles.embeddedPlayer, 'rounded-none')}>
      <div
        className={cx(styles.controls, '!rounded-none bg-[var(--grayscale-1)]')}
        onClick={handlePlayPause}
      >
        {!playing ? <Play width={14} height={14} /> : <Pause width={14} height={14} />}
      </div>
      <div ref={containerRef} className={styles.waves}></div>
      <div className={cx(styles.timer, '!rounded-none bg-[var(--grayscale-1)]')}>
        <span className={cx(styles.current, 'ml-2 p-1')}>{secondsToTimestamp(secondsElapsed)}</span>
        /<span className={styles.duration}>{secondsToTimestamp(duration)}</span>
      </div>
    </div>
  );
};

export default EmbeddedPlayer;
